import { useState, useRef } from 'react';
import { X, Camera, AlertCircle } from 'lucide-react';
import { checkImageSafety } from '@/services/ai/GuardAI';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';

interface CreatePostModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (newPost: any) => void;
}

export function CreatePostModal({ isOpen, onClose, onSuccess }: CreatePostModalProps) {
    const { account, profile } = useAuthStore();
    const [caption, setCaption] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('Image must be under 5MB.');
                return;
            }
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
            setError(null);
        }
    };

    const handlePost = async () => {
        if (!image || !account) return;

        setIsProcessing(true);
        setError(null);

        try {
            // 1. AI Safety Check
            const safety = await checkImageSafety(image);

            if (!safety.safe) {
                setError(`BLOCKED: Inappropriate content detected. ${safety.reason || 'Safety Violation'}. Continuous violations will result in a ban.`);
                setIsProcessing(false);
                return;
            }

            // 2. Upload to Supabase
            const fileExt = image.name.split('.').pop();
            const fileName = `posts/${account.id}-${Date.now()}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('posts')
                .upload(fileName, image);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('posts')
                .getPublicUrl(fileName);

            // 3. Create Post Object (In a real app, this would go to a 'posts' table)
            const newPost = {
                id: Date.now(),
                username: profile?.name || 'Wrestler',
                location: profile?.team || 'Home Gym',
                imageUrl: publicUrl,
                caption: caption,
                likes: 0,
                timeAgo: 'Just now',
                isVerified: false
            };

            onSuccess(newPost);
            onClose();
            // Reset state
            setCaption('');
            setImage(null);
            setPreviewUrl(null);
        } catch (err) {
            console.error('Failed to create post:', err);
            setError('Failed to upload post. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-lg bg-bg-surface border border-white/10 rounded-3xl overflow-hidden shadow-2xl animate-fade-in-up">
                <div className="flex items-center justify-between p-4 border-b border-white/5">
                    <button onClick={onClose} className="p-2 text-text-muted hover:text-white">
                        <X size={20} />
                    </button>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-white">New Post</h3>
                    <button
                        onClick={handlePost}
                        disabled={!image || isProcessing}
                        className="px-4 py-1.5 rounded-full bg-gold-500 text-bg-900 text-xs font-black uppercase tracking-wider disabled:opacity-30 disabled:grayscale transition-all"
                    >
                        {isProcessing ? 'Checking...' : 'Post'}
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error Banner */}
                    {error && (
                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium flex items-start gap-2 animate-shake">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            <p>{error}</p>
                        </div>
                    )}

                    {/* Image Preview / Drop Area */}
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square w-full rounded-2xl bg-black/20 border-2 border-dashed border-white/10 flex flex-col items-center justify-center cursor-pointer hover:border-gold-500/50 transition-colors overflow-hidden relative group"
                    >
                        {previewUrl ? (
                            <img src={previewUrl} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-center">
                                <Camera size={40} className="mx-auto mb-2 text-text-muted group-hover:text-gold-500 transition-colors" />
                                <p className="text-xs font-bold text-text-muted uppercase tracking-widest">Select Image</p>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/*"
                            className="hidden"
                        />
                    </div>

                    {/* Caption Input */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text- gold-500 uppercase tracking-wider ml-1">Caption</label>
                        <textarea
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Write a caption..."
                            className="w-full h-24 bg-black/20 border border-white/10 rounded-xl p-4 text-sm text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors resize-none"
                        />
                    </div>

                    {/* Safety Notice */}
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className="w-8 h-8 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500">
                            <AlertCircle size={16} />
                        </div>
                        <p className="text-[9px] text-text-muted leading-relaxed uppercase tracking-tighter">
                            A.I. Moderation is active. Inappropriate content will be flagged, reported, and your account will be permanently banned.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
